import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import { fetchAdminDriverTownCoverage } from '../services/api/admin';
import type { AdminDriverTownCoverage } from '../types/admin';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

const DEFAULT_FILTERS = {
  query: '',
  onboardingsStep: 'COMPLETED',
  onboardingReviewStatus: 'APPROVED',
  accountType: '',
  enabled: 'true',
  province: '',
  country: '',
  includeUnspecified: false,
};

function formatNumber(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-ZA').format(Number(value ?? 0));
}

function formatLabel(value: string | null | undefined, fallback = 'Not specified'): string {
  return value?.trim() || fallback;
}

function formatVehicleMix(vehicleTypeCounts: Record<string, number>): string {
  const entries = Object.entries(vehicleTypeCounts ?? {}).filter(([, count]) => Number(count) > 0);
  if (!entries.length) return 'No vehicle details';
  return entries
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([type, count]) => `${type.replaceAll('_', ' ')} ${formatNumber(count)}`)
    .join(', ');
}

function campaignLine(row: AdminDriverTownCoverage): string {
  const count = Number(row.approvedEnabledCount ?? 0);
  const driverWord = count === 1 ? 'driver' : 'drivers';
  return `${formatNumber(count)} approved ${driverWord} in ${row.town} ready for your request.`;
}

const AdminDriverCoveragePage: React.FC = () => {
  const [coverage, setCoverage] = useState<AdminDriverTownCoverage[]>([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const params = useMemo(
    () => ({
      ...(filters.query.trim() ? { query: filters.query.trim() } : {}),
      ...(filters.onboardingsStep ? { onboardingsStep: filters.onboardingsStep } : {}),
      ...(filters.onboardingReviewStatus ? { onboardingReviewStatus: filters.onboardingReviewStatus } : {}),
      ...(filters.accountType ? { accountType: filters.accountType } : {}),
      ...(filters.enabled ? { enabled: filters.enabled === 'true' } : {}),
      ...(filters.province.trim() ? { province: filters.province.trim() } : {}),
      ...(filters.country.trim() ? { country: filters.country.trim() } : {}),
      includeUnspecified: filters.includeUnspecified,
    }),
    [filters],
  );

  const totals = useMemo(
    () =>
      coverage.reduce(
        (acc, row) => ({
          coveredTowns: acc.coveredTowns + (row.town && row.town !== 'Unspecified' ? 1 : 0),
          totalDrivers: acc.totalDrivers + Number(row.totalCount ?? 0),
          approvedEnabled: acc.approvedEnabled + Number(row.approvedEnabledCount ?? 0),
          submittedPending: acc.submittedPending + Number(row.submittedPendingCount ?? 0),
          onlineNow: acc.onlineNow + Number(row.onlineNowCount ?? 0),
        }),
        {
          approvedEnabled: 0,
          coveredTowns: 0,
          onlineNow: 0,
          submittedPending: 0,
          totalDrivers: 0,
        },
      ),
    [coverage],
  );

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const rows = await fetchAdminDriverTownCoverage(params);
        if (!mounted) return;
        setCoverage(rows);
      } catch (loadError: any) {
        if (!mounted) return;
        setError(loadError.message || 'Could not load driver town coverage.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [params]);

  const refresh = async () => {
    setRefreshing(true);
    setError('');
    try {
      setCoverage(await fetchAdminDriverTownCoverage(params));
    } catch (loadError: any) {
      setError(loadError.message || 'Could not refresh driver town coverage.');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <AdminShell
      badge="Driver Coverage"
      copy="View declared driver operating towns and campaign-safe driver counts from approved, enabled driver profiles."
      title="Driver coverage"
    >
      {error ? <div className="register-inline-error">{error}</div> : null}

      <div className="admin-grid admin-grid-cards admin-coverage-kpis">
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Covered towns</span>
          <strong>{formatNumber(totals.coveredTowns)}</strong>
          <span className="admin-stat-note">Declared towns represented by the current filters.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Approved enabled</span>
          <strong>{formatNumber(totals.approvedEnabled)}</strong>
          <span className="admin-stat-note">Campaign-safe driver count.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Pending review</span>
          <strong>{formatNumber(totals.submittedPending)}</strong>
          <span className="admin-stat-note">Submitted driver supply still in review.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Online now</span>
          <strong>{formatNumber(totals.onlineNow)}</strong>
          <span className="admin-stat-note">Online drivers from these home-base towns.</span>
        </div>
      </div>

      <section className="admin-panel admin-coverage-filter-panel">
        <div className="admin-toolbar admin-coverage-toolbar">
          <input
            className="register-input admin-toolbar-wide"
            onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value }))}
            placeholder="Search by driver name, email, or phone"
            value={filters.query}
          />
          <select
            className="register-input"
            onChange={(event) => setFilters((current) => ({ ...current, onboardingsStep: event.target.value }))}
            value={filters.onboardingsStep}
          >
            <option value="">All steps</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="COMPLETED">Completed</option>
          </select>
          <select
            className="register-input"
            onChange={(event) => setFilters((current) => ({ ...current, onboardingReviewStatus: event.target.value }))}
            value={filters.onboardingReviewStatus}
          >
            <option value="">All review states</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
          <select
            className="register-input"
            onChange={(event) => setFilters((current) => ({ ...current, accountType: event.target.value }))}
            value={filters.accountType}
          >
            <option value="">All account types</option>
            <option value="INDIVIDUAL_OWNER">Individual owner</option>
            <option value="COMPANY_DRIVER">Company driver</option>
          </select>
          <select
            className="register-input"
            onChange={(event) => setFilters((current) => ({ ...current, enabled: event.target.value }))}
            value={filters.enabled}
          >
            <option value="">Enabled or disabled</option>
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
          </select>
          <input
            className="register-input"
            onChange={(event) => setFilters((current) => ({ ...current, province: event.target.value }))}
            placeholder="Province"
            value={filters.province}
          />
          <label className="admin-checkbox-row admin-coverage-checkbox">
            <input
              checked={filters.includeUnspecified}
              onChange={(event) => setFilters((current) => ({ ...current, includeUnspecified: event.target.checked }))}
              type="checkbox"
            />
            Include unspecified
          </label>
          <button className="register-secondary-button" disabled={refreshing || loading} onClick={refresh} type="button">
            {refreshing ? 'Refreshing' : 'Refresh'}
          </button>
        </div>
      </section>

      <section className="admin-panel">
        <div className="admin-panel-header">
          <div>
            <h2 className="admin-panel-title">Town coverage</h2>
            <p className="admin-panel-copy">Counts are grouped by the driver's declared operating town, not their latest GPS position.</p>
          </div>
        </div>

        {loading ? (
          <div className="register-loader" />
        ) : (
          <div className="admin-coverage-table">
            <div className="admin-coverage-table-head">
              <span>Town</span>
              <span>Approved</span>
              <span>Pending</span>
              <span>Online</span>
              <span>Vehicles</span>
              <span>Campaign line</span>
            </div>
            {coverage.map((row) => (
              <div className="admin-coverage-table-row" key={`${row.town}-${row.province ?? ''}-${row.country ?? ''}`}>
                <div>
                  <strong>{row.town}</strong>
                  <small>
                    {formatLabel(row.province)} / {formatLabel(row.country, 'Country not set')}
                  </small>
                  {row.latitude !== null && row.longitude !== null ? (
                    <small>
                      {row.latitude?.toFixed(5)}, {row.longitude?.toFixed(5)}
                    </small>
                  ) : null}
                </div>
                <strong>{formatNumber(row.approvedEnabledCount)}</strong>
                <strong>{formatNumber(row.submittedPendingCount)}</strong>
                <strong>{formatNumber(row.onlineNowCount)}</strong>
                <small>{formatVehicleMix(row.vehicleTypeCounts)}</small>
                <div className="admin-coverage-action">
                  <small className="admin-coverage-copy">{campaignLine(row)}</small>
                  <Link
                    className="admin-link-button"
                    to={`/admin/drivers?${new URLSearchParams({
                      town: row.town,
                      ...(row.province ? { province: row.province } : {}),
                      ...(row.country ? { country: row.country } : {}),
                    }).toString()}`}
                  >
                    View drivers
                  </Link>
                </div>
              </div>
            ))}
            {!coverage.length ? <p className="admin-empty">No driver coverage matches the current filters.</p> : null}
          </div>
        )}
      </section>
    </AdminShell>
  );
};

export default AdminDriverCoveragePage;
